import React, { useMemo } from 'react';
import { SelectMulti } from '@pileus-cloud/anodot-frontend-common';
import { uniqBy } from 'lodash';
import moment from 'moment';
import DatePickerFilter from 'shared/components/DatePickerFilter';
import styles from './filters.module.scss';
import { marginTypeToDisplayName } from '../billingRulesHelpers';
import { useRootStore } from 'app/contexts/RootStoreContext';
import { getLinkedAccountName } from 'shared/utils/cloudUtils';
import { useDivisions } from 'shared/hooks/react-query/useDivisions';

const Filters = ({ filters, setFilters, billingRules }) => {
  const { usersStore } = useRootStore();
  const { getDivisions } = useDivisions();
  const { data: divisionsData = [] } = getDivisions();

  const ruleNames = useMemo(
    () =>
      uniqBy(
        billingRules.map((rule) => ({ label: rule.description.name, value: rule.description.name })),
        'value',
      ),
    [billingRules],
  );
  const customerNames = useMemo(
    () =>
      uniqBy(
        billingRules
          .map((rule) => rule.scope.customerNames)
          .flat()
          .map((v) => ({
            label: divisionsData.rawDivisionGroups?.find((div) => div.divisionName == v)?.divisionNameDisplay || v,
            value: v,
          })),
        'value',
      ),
    [billingRules, divisionsData],
  );
  const linkedAccountIds = useMemo(
    () =>
      uniqBy(
        billingRules
          .map((rule) => rule.scope.linkedAccountIds)
          .flat()
          .map((v) => ({ label: v, value: v })),
        'value',
      ),
    [billingRules],
  );
  const marginTypes = useMemo(
    () =>
      uniqBy(
        billingRules.map((rule) => rule.margin.type).map((v) => ({ label: marginTypeToDisplayName[v], value: v })),
        'value',
      ),
    [billingRules],
  );
  const ruleIds = useMemo(
    () => billingRules.map((rule) => rule.ruleId).map((v) => ({ label: v, value: v })),
    [billingRules],
  );

  return (
    <div className={styles.container}>
      <div>
        <SelectMulti
          label="Rule Name"
          options={ruleNames}
          handleSelectionChange={(vals) => {
            setFilters({ ...filters, ruleNames: vals });
          }}
          selected={filters.ruleNames}
        />
      </div>
      <div>
        <SelectMulti
          label="Customer Name"
          options={customerNames}
          handleSelectionChange={(vals) => {
            setFilters({ ...filters, customerNames: vals });
          }}
          selected={filters.customerNames}
        />
      </div>
      <div>
        <SelectMulti
          label={getLinkedAccountName(usersStore.currDispUserCloudAccountType)}
          options={linkedAccountIds}
          handleSelectionChange={(vals) => {
            setFilters({ ...filters, linkedAccountIds: vals });
          }}
          selected={filters.linkedAccountIds}
        />
      </div>
      <div>
        <SelectMulti
          label="Margin Type"
          options={marginTypes}
          handleSelectionChange={(vals) => {
            setFilters({ ...filters, marginTypes: vals });
          }}
          selected={filters.marginTypes}
        />
      </div>
      <div>
        <SelectMulti
          label="Rule ID"
          options={ruleIds}
          handleSelectionChange={(vals) => {
            setFilters({ ...filters, ruleIds: vals });
          }}
          selected={filters.ruleIds}
        />
      </div>
      <div>
        <DatePickerFilter
          isDateRangeError={false}
          onDateChange={({ startDate, endDate }) => {
            setFilters({
              ...filters,
              startMonth: moment(startDate).format('YYYY-MM'),
              endMonth: moment(endDate).format('YYYY-MM'),
            });
          }}
          startDate={filters.startMonth}
          endDate={filters.endMonth}
          isMaxDateNeeded={false}
          currPeriodGranLevel="month"
          dateFormat="MMM YYYY"
          andtLook
        />
      </div>
    </div>
  );
};

export default Filters;
